import { cn } from '../../utils/cn';
import { Spinner } from './Spinner';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  /** Size preset */
  size?: 'sm' | 'md' | 'lg';
  /** Show spinner and disable interactions */
  isLoading?: boolean;
  /** Optional icon element rendered before children */
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-accent-500 text-text-on-accent hover:bg-accent-400 active:bg-accent-600 active:scale-[0.98]',
  secondary:
    'bg-secondary-solid text-text-on-secondary hover:bg-secondary-solid-hover active:bg-secondary-solid-hover',
  ghost:
    'bg-transparent text-text-secondary hover:bg-bg-hover hover:text-text-primary',
  danger:
    'bg-error-solid text-text-on-error hover:bg-error-solid-hover active:bg-error-solid-hover',
  outline:
    'bg-transparent text-text-accent border border-accent-500 hover:bg-accent-500/10',
};

const sizeStyles: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 h-8 text-xs min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)] lg:min-h-0 lg:min-w-0',
  md: 'px-4 h-10 text-sm min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)] lg:min-h-0 lg:min-w-0',
  lg: 'px-6 h-12 text-base min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-semibold',
        'transition-all duration-200 cursor-pointer',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Spinner size={size === 'sm' ? 'xs' : 'sm'} className="text-current" />
          {children}
        </>
      ) : (
        <>
          {icon && <span className="shrink-0">{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
}

export default Button;
